import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  LinearProgress,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { db } from '../../../../firebase';
import { collection, addDoc, writeBatch, Timestamp, doc } from 'firebase/firestore';
import axios from 'axios';

interface AIImportDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

interface ParsedContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  tags: string[];
  notes: string;
  confidence: number;
  originalText: string;
  isValid: boolean;
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  importDate?: string;
}

const EXAMPLE_PROMPT = `John Smith - Marketing Director at ABC Corp
john.smith@abccorp.com, (555) 123-4567
Met at industry conference, interested in our premium tier

Sarah Johnson
sarah.j@gmail.com
Referred by Mike, potential partner for upcoming campaign`;

const AIImportDialog: React.FC<AIImportDialogProps> = ({ open, onClose, organizationId }) => {
  const [inputText, setInputText] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{success: number; total: number} | null>(null);
  const [editingContact, setEditingContact] = useState<ParsedContact | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isCSVFormat, setIsCSVFormat] = useState(false);
  
  // Get current date in EST timezone
  const getCurrentESTDate = (): string => {
    const now = new Date();
    
    // Format in ISO with EST timezone indicator
    return now.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/(\d+)\/(\d+)\/(\d+),\s(\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6-04:00');
  };

  // Handle text input change
  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(event.target.value);
    
    // Detect if input looks like CSV
    const lines = event.target.value.trim().split('\n');
    if (lines.length > 1 && lines[0].includes(',') && lines[1].includes(',')) {
      setIsCSVFormat(true);
    } else {
      setIsCSVFormat(false);
    }
  };
  
  // Handle "Use Example" button
  const handleUseExample = () => {
    setInputText(EXAMPLE_PROMPT);
  };
  
  // Check if the AI analysis can be run
  const canAnalyze = inputText.trim().length > 0 && !isAnalyzing;
  
  // Parse CSV function
  const parseCSV = (csvText: string): ParsedContact[] => {
     const lines = csvText.trim().split('\n');

     if (lines.length <= 1) {
        return [];
     }

     // Parse header to identify column positions
     const headerLine = lines[0];
     const headers = headerLine.split(',').map((h) => h.trim().toLowerCase());

     // Find column indexes
     const findColumnIndex = (possibleNames: string[]): number => {
        return headers.findIndex((header) => possibleNames.some((name) => header.includes(name)));
     };

     const firstNameIdx = findColumnIndex(['first name', 'firstname', 'first_name', 'given name']);
     const lastNameIdx = findColumnIndex(['last name', 'lastname', 'last_name', 'surname']);
     const emailIdx = findColumnIndex(['email', 'e-mail', 'email address']);
     const phoneIdx = findColumnIndex(['phone', 'telephone', 'mobile', 'cell']);
     const companyIdx = findColumnIndex(['company', 'organization', 'business', 'employer']);
     const titleIdx = findColumnIndex(['title', 'job title', 'position', 'role']);
     const eventNameIdx = findColumnIndex(['event name', 'eventname', 'event_name', 'conference']);
     const eventDateIdx = findColumnIndex(['event date', 'eventdate', 'event_date', 'date']);
     const ticketTypeIdx = findColumnIndex(['ticket type', 'ticket', 'pass type', 'membership']);
     const statusIdx = findColumnIndex(['status', 'order status', 'check-in status']);

     if (firstNameIdx === -1 || lastNameIdx === -1 || emailIdx === -1) {
        return [];
     }

     const importDate = getCurrentESTDate();
     const contacts: ParsedContact[] = [];

     // Skip header and parse data rows
     for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;

        // Handle quoted fields correctly
        const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
        const cleanValues = values.map((val) => val.replace(/^"|"$/g, '').trim());

        if (cleanValues.length < Math.max(firstNameIdx, lastNameIdx, emailIdx) + 1) {
           continue;
        }

        const eventName = eventNameIdx > -1 ? cleanValues[eventNameIdx] : '';
        const eventDate = eventDateIdx > -1 ? cleanValues[eventDateIdx] : '';
        const ticketType = ticketTypeIdx > -1 ? cleanValues[ticketTypeIdx] : '';

        const contact: ParsedContact = {
           firstName: cleanValues[firstNameIdx] || 'Unknown',
           lastName: cleanValues[lastNameIdx] || '',
           email: cleanValues[emailIdx] || '',
           phone: phoneIdx > -1 ? cleanValues[phoneIdx] || '' : '',
           company: companyIdx > -1 ? cleanValues[companyIdx] || '' : '',
           title: titleIdx > -1 ? cleanValues[titleIdx] || '' : ticketType, // Use ticket type as fallback for title
           tags: ['csv-imported'],
           notes: statusIdx > -1 ? `Status: ${cleanValues[statusIdx]}` : '',
           confidence: 100, // High confidence since this is structured data
           originalText: line,
           isValid: Boolean(cleanValues[emailIdx] && cleanValues[emailIdx].includes('@')),
           eventName,
           eventDate,
           eventDescription: ticketType ? `Attendee type: ${ticketType}` : '',
           importDate,
        };

        // Add ticket type as a tag if available
        if (ticketType) {
           contact.tags.push(ticketType.toLowerCase().replace(/\s+/g, '-'));
        }

        // Add check-in status as a tag if available
        if (statusIdx > -1 && cleanValues[statusIdx].toLowerCase().includes('checked in')) {
           contact.tags.push('checked-in');
        }

        contacts.push(contact);
     }

     return contacts;
  };

  // Handle AI analysis button
  const handleAnalyzeWithAI = async () => {
     if (!canAnalyze) return;

     setIsAnalyzing(true);
     setErrorMessage(null);

     try {
        // If the input looks like CSV format, use the CSV parser
        if (isCSVFormat) {
           const parsedContacts = parseCSV(inputText);

           if (parsedContacts.length === 0) {
              setErrorMessage('Could not parse CSV. Please check the format and try again.');
              setIsAnalyzing(false);
              return;
           }

           const validContacts = parsedContacts.filter((contact) => contact.isValid);

           if (validContacts.length === 0 && parsedContacts.length > 0) {
              setErrorMessage(
                 'Contacts were found but none had valid email addresses. Please edit the contacts manually.'
              );
           }

           setParsedContacts(parsedContacts);
           setIsAnalyzing(false);
           return;
        }

        // If not CSV, proceed with AI analysis

        // Call our backend API instead of Claude directly
        const response = await axios.post(
           `${process.env.REACT_APP_API_URL}/api/ai/parse-contacts`,
           { textInput: inputText },
           {
              headers: {
                 'Content-Type': 'application/json',
              },
           }
        );

        if (response.data.error) {
           throw new Error(response.data.error);
        }

        // Get the parsed contacts from the response
        const rawParsedContacts = response.data.contacts;

        if (
           !rawParsedContacts ||
           !Array.isArray(rawParsedContacts) ||
           rawParsedContacts.length === 0
        ) {
           setErrorMessage(
              'No contacts found in the text. Please try different content or format.'
           );
           setIsAnalyzing(false);
           return;
        }

        // Convert to our ParsedContact interface
        const parsedContacts: ParsedContact[] = rawParsedContacts.map((contact: any) => ({
           firstName: contact.firstName || 'Unknown',
           lastName: contact.lastName || '',
           email: contact.email || '',
           phone: contact.phone || '',
           company: contact.company || '',
           title: contact.title || contact.jobTitle || '',
           tags: Array.isArray(contact.tags) ? contact.tags : ['ai-imported'],
           notes: contact.notes || '',
           confidence: typeof contact.confidence === 'number' ? contact.confidence : 70,
           originalText: contact.originalText || inputText,
           isValid: !!contact.email && contact.email.includes('@'),
           eventName: contact.eventName || '',
           eventDate: contact.eventDate || '',
           eventDescription: contact.eventDescription || '',
           importDate: getCurrentESTDate(),
        }));

        const validContacts = parsedContacts.filter((contact) => contact.isValid);

        if (validContacts.length === 0 && parsedContacts.length > 0) {
           setErrorMessage(
              'Contacts were found but none had valid email addresses. Please edit the contacts manually.'
           );
        }

        setParsedContacts(parsedContacts);
     } catch (error: any) {
        console.error('Error analyzing text with AI:', error);
        let errorMsg =
           'Failed to analyze the text. Please try again or format your input differently.';

        if (error.response?.data?.message) {
           errorMsg = `Error: ${error.response.data.message}`;
        } else if (error.message) {
           errorMsg = `Error: ${error.message}`;
        }

        setErrorMessage(errorMsg);
     } finally {
        setIsAnalyzing(false);
     }
  };
  
  // Handle edit contact button
  const handleEditContact = (contact: ParsedContact, index: number) => {
    setEditingContact({...contact});
    setEditingIndex(index);
  };
  
  // Handle save edited contact
  const handleSaveEditedContact = () => {
    if (editingContact && editingIndex !== null) {
      const updatedContacts = [...parsedContacts];
      updatedContacts[editingIndex] = editingContact;
      setParsedContacts(updatedContacts);
      setEditingContact(null);
      setEditingIndex(null);
    }
  };
  
  // Handle remove contact button
  const handleRemoveContact = (index: number) => {
    const updatedContacts = [...parsedContacts];
    updatedContacts.splice(index, 1);
    setParsedContacts(updatedContacts);
  };
  
  // Handle import contacts button
  const handleImportContacts = async () => {
    const validContacts = parsedContacts.filter(contact => contact.isValid);
    
    if (validContacts.length === 0) {
      setErrorMessage('No valid contacts to import');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage(null);
    
    try {
       // Use batched writes for better performance
       const batch = writeBatch(db);
       const contactsRef = collection(db, `organizations/${organizationId}/contacts`);

       // Current EST date/time
       const importDate = getCurrentESTDate();

       for (const parsedContact of validContacts) {
          const contact = {
             firstName: parsedContact.firstName,
             lastName: parsedContact.lastName,
             email: parsedContact.email,
             phone: parsedContact.phone || '',
             company: parsedContact.company || '',
             title: parsedContact.title || '',
             tags: parsedContact.tags || ['ai-imported'],
             notes: parsedContact.notes || '',
             engagementScore: 0,
             createdAt: Timestamp.now(),
             source: 'ai_import',
             consentStatus: 'unknown',
             eventName: parsedContact.eventName || '',
             eventDate: parsedContact.eventDate || '',
             eventDescription: parsedContact.eventDescription || '',
             importDate,
          };

          // Add to batch
          const newContactRef = doc(contactsRef);
          batch.set(newContactRef, contact);
       }

       // Commit the batch

       await batch.commit();

       // Trigger a refresh of the contacts list by adding a refresh parameter to the URL
       const url = new URL(window.location.href);
       url.searchParams.set('refresh', 'true');
       window.history.pushState({}, '', url.toString());

       // Force a window event to trigger the useEffect in the parent component
       window.dispatchEvent(new Event('popstate'));

       setImportResult({
          success: validContacts.length,
          total: parsedContacts.length,
       });
    } catch (error) {
      console.error('Error importing contacts:', error);
      setErrorMessage('Failed to import contacts. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Reset dialog state
  const handleClose = () => {
    onClose();
    setInputText('');
    setParsedContacts([]);
    setIsAnalyzing(false);
    setIsSubmitting(false);
    setErrorMessage(null);
    setImportResult(null);
    setEditingContact(null);
    setEditingIndex(null);
  };
  
  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return '#4caf50';
    if (confidence >= 70) return '#ff9800';
    return '#f44336';
  };
  
  return (
    <Dialog
      open={open}
      onClose={!isAnalyzing && !isSubmitting ? handleClose : undefined}
      maxWidth="md"
      fullWidth
      aria-labelledby="ai-import-dialog-title"
    >
      <DialogTitle id="ai-import-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoAwesomeIcon sx={{ mr: 1 }} />
            AI-Powered Contact Import
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            disabled={isAnalyzing || isSubmitting}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        {importResult ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Import Complete!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Successfully imported {importResult.success} contacts
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
              sx={{ mt: 3 }}
            >
              Done
            </Button>
          </Box>
        ) : (
          <>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            
            {editingContact !== null ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Edit Contact
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editingContact.firstName}
                      onChange={(e) => setEditingContact({...editingContact, firstName: e.target.value})}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editingContact.lastName}
                      onChange={(e) => setEditingContact({...editingContact, lastName: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={editingContact.email}
                      onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                      required
                      error={!editingContact.email || !editingContact.email.includes('@')}
                      helperText={!editingContact.email || !editingContact.email.includes('@') ? 'Valid email is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={editingContact.phone || ''}
                      onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={editingContact.company || ''}
                      onChange={(e) => setEditingContact({...editingContact, company: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={editingContact.title || ''}
                      onChange={(e) => setEditingContact({...editingContact, title: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tags (comma separated)"
                      value={editingContact.tags.join(', ')}
                      onChange={(e) => setEditingContact({
                        ...editingContact, 
                        tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={editingContact.notes || ''}
                      onChange={(e) => setEditingContact({...editingContact, notes: e.target.value})}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Event Name"
                      value={editingContact.eventName || ''}
                      onChange={(e) => setEditingContact({...editingContact, eventName: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Event Date"
                      value={editingContact.eventDate || ''}
                      onChange={(e) => setEditingContact({...editingContact, eventDate: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Event Description"
                      value={editingContact.eventDescription || ''}
                      onChange={(e) => setEditingContact({...editingContact, eventDescription: e.target.value})}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Import Date"
                      value={editingContact.importDate || ''}
                      onChange={(e) => setEditingContact({...editingContact, importDate: e.target.value})}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button 
                    onClick={() => {
                      setEditingContact(null);
                      setEditingIndex(null);
                    }}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained"
                    onClick={handleSaveEditedContact}
                    disabled={!editingContact.email || !editingContact.email.includes('@')}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            ) : parsedContacts.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  AI-Parsed Contacts ({parsedContacts.length})
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review and edit the extracted contact information before importing.
                </Typography>
                
                <List sx={{ mb: 3 }}>
                  {parsedContacts.map((contact, index) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                              {contact.firstName.charAt(0)}{contact.lastName ? contact.lastName.charAt(0) : ''}
                            </Avatar>
                            <Typography variant="h6">
                              {contact.firstName} {contact.lastName}
                            </Typography>
                          </Box>
                          <Box>
                            <Tooltip title="Edit contact">
                              <IconButton size="small" onClick={() => handleEditContact(contact, index)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove contact">
                              <IconButton size="small" onClick={() => handleRemoveContact(index)}>
                                <HighlightOffIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Email: <b>{contact.email}</b>
                            </Typography>
                          </Grid>
                          {contact.phone && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Phone: <b>{contact.phone}</b>
                              </Typography>
                            </Grid>
                          )}
                          {contact.company && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Company: <b>{contact.company}</b>
                              </Typography>
                            </Grid>
                          )}
                          {contact.title && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Title: <b>{contact.title}</b>
                              </Typography>
                            </Grid>
                          )}
                          {contact.eventName && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Event Name: <b>{contact.eventName}</b>
                              </Typography>
                            </Grid>
                          )}
                          {contact.eventDate && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Event Date: <b>{contact.eventDate}</b>
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        
                        {contact.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                            Notes: {contact.notes}
                          </Typography>
                        )}
                        
                        {contact.eventDescription && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                            Event Description: {contact.eventDescription}
                          </Typography>
                        )}
                        
                        {contact.tags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 2 }}>
                            {contact.tags.map((tag, tagIndex) => (
                              <Chip 
                                key={tagIndex} 
                                label={tag} 
                                size="small" 
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                              AI Confidence:
                            </Typography>
                            <Box sx={{ width: 100, mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={contact.confidence} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 5,
                                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getConfidenceColor(contact.confidence)
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {contact.confidence}%
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </List>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setParsedContacts([]);
                      setInputText('');
                    }}
                    sx={{ mr: 1 }}
                  >
                    Start Over
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={parsedContacts.length === 0 || isSubmitting}
                    onClick={handleImportContacts}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <PersonAddIcon />}
                  >
                    {isSubmitting ? 'Importing...' : `Import ${parsedContacts.length} Contacts`}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Paste unstructured contact data from emails, business cards, or notes. Our AI will automatically parse names, emails, and other information.
                  </Typography>
                </Alert>
                
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  placeholder="Paste contact information here (names, emails, phone numbers, notes, etc.)"
                  value={inputText}
                  onChange={handleTextChange}
                  variant="outlined"
                  disabled={isAnalyzing}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ position: 'absolute', right: 8, bottom: 8 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={handleUseExample}
                          disabled={isAnalyzing}
                        >
                          Use Example
                        </Button>
                      </Box>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />
                
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!canAnalyze}
                    onClick={handleAnalyzeWithAI}
                    startIcon={isAnalyzing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                    size="large"
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      {!parsedContacts.length && !editingContact && !importResult && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isAnalyzing}>
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AIImportDialog;
